/**
 * Reading a run's `facts` blob for the ticket workspace.
 *
 * `facts` is JSON written by two stages — intake and the agent — and it is
 * deliberately not typed at the database boundary, because a run recorded last
 * month should still render after the shape changes. So everything here reads
 * defensively and **omits what it cannot find rather than showing a zero.**
 *
 * That is the rule worth stating: a missing fact and a fact whose value is zero
 * look identical once rendered, and only one of them means "we checked". An
 * order total of $0.00 where we simply never resolved the order is how an
 * operator approves a refund against nothing.
 */

export interface FactRow {
  label: string;
  value: string;
  tone?: 'fact' | 'held' | 'stop';
}

export interface ProposedWrite {
  tool: string;
  arguments: Record<string, unknown>;
  round: number;
}

export interface RuleViolation {
  ruleId: string;
  severity: string;
  evidence: string;
  explanation: string;
}

export interface RunFacts {
  rows: FactRow[];
  proposedWrites: ProposedWrite[];
  violations: RuleViolation[];
  /** The offer policy authorised, for the header. */
  offerPercent: number | null;
  offerAmountCents: number | null;
  /** Why no offer, when there was none. */
  noOfferReason: string | null;
  stopReason: string | null;
  rounds: number | null;
  indeterminate: string[];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function formatUsdCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

const HOLD_DROPPED: Record<string, string> = {
  superseded: 'the customer wrote again, and the newer reply answers both',
  ticket_taken_over: 'the ticket was handed off or closed during the wait',
  already_sent: 'a person sent it during the wait',
};

/**
 * A marketing unsubscribe and nothing else: code did the whole job (Iterable,
 * Suppression Hub, a fixed reply) and no model drafted anything.
 */
function isCodeHandledUnsubscribe(facts: Record<string, unknown>): boolean {
  // A data-deletion request is handled the same way (Deivydas 2026-10-16).
  return (
    (facts.intent === 'marketing_unsubscribe' || facts.intent === 'unsubscribe_privacy') &&
    asRecord(facts.marketingUnsubscribe) !== null
  );
}

/**
 * The Routing line for a run (Deivydas 2026-10-14, #1927954): a run with no
 * workflow is usually one that needed none — say which, rather than a bare
 * "no workflow" that reads like a failure.
 */
export function routingLabel(workflowSlug: string | null, raw: unknown): string {
  if (workflowSlug) return workflowSlug;
  const facts = asRecord(raw) ?? {};
  if (isCodeHandledUnsubscribe(facts)) return 'handled in code: marketing unsubscribe (no workflow needed)';
  if (facts.kind === 'follow_up_nudge') {
    const why = asString(facts.why);
    return `follow-up to a quiet customer${why ? `: ${why}` : ''} (no workflow needed)`;
  }
  if (facts.repeatedMessage !== undefined) return 'same message as 2+ tickets today, closed with a note (no workflow needed)';
  if (facts.automatedMessage === true) return 'autoresponder, closed with a note (no workflow needed)';
  if (facts.ticketWithHuman === true) return 'ticket is with a person (Zoye stays silent)';
  const disposition = asString(asRecord(facts.disposition)?.kind);
  if (disposition === 'close') return 'solved without a reply (no workflow needed)';
  if (disposition === 'hand_off') return 'handed to a person by topic (no workflow needed)';
  return 'no workflow';
}

export function readRunFacts(raw: unknown): RunFacts {
  const facts = asRecord(raw) ?? {};
  const rows: FactRow[] = [];
  // Intake looks up the orders before it knows the topic; an unsubscribe never
  // uses them, so "which order" would only be noise there.
  const ordersMatter = !isCodeHandledUnsubscribe(facts);

  const email = asString(facts.resolvedEmail);
  const identitySource = asString(facts.identitySource);
  if (email) {
    rows.push({
      label: 'Email',
      value: identitySource
        ? `${email} · ${identitySource === 'channel' ? 'from the ticket, not asked' : `from the ${identitySource.replace(/_/g, ' ')}`}`
        : email,
      tone: 'fact',
    });
  }

  // The account was found on a sister brand's store, which then handled the
  // whole conversation (#1923345, 2026-10-16).
  const accountBrand = asRecord(facts.accountBrand);
  const storeName = asString(accountBrand?.name) ?? asString(accountBrand?.slug);
  if (storeName) {
    rows.push({
      label: 'Store',
      value: `${storeName} · the account is on this brand's store, not the ticket's`,
      tone: 'held',
    });
  }

  const orderId = asString(facts.latestOrderId);
  const orderTotal = asNumber(facts.latestOrderTotalCents);
  if (orderId || orderTotal !== null) {
    // Both parts are conditional: an order we found but could not price is a
    // real state, and printing $0.00 for it would be a lie.
    const parts = [orderId ? `#${orderId}` : 'order id unknown'];
    if (orderTotal !== null) parts.push(formatUsdCents(orderTotal));
    const days = asNumber(facts.daysSinceLatestOrder);
    if (days !== null) parts.push(`${days} days ago`);
    rows.push({ label: 'Latest order', value: parts.join(' · ') });
  }

  // How the order under discussion was picked — the customer's id, their own
  // words (with the words), or ambiguity with a candidate list. The
  // #1024/#1025 class was undiagnosable without it: the trace showed WHICH
  // order was answered about but never WHY.
  const identification = ordersMatter ? asRecord(facts.orderIdentification) : null;
  const identKind = asString(identification?.kind);
  if (identKind === 'explicit') {
    rows.push({ label: 'Order picked by', value: 'the id the customer wrote' });
  } else if (identKind === 'matched') {
    const matched = Array.isArray(identification?.matched)
      ? identification.matched.filter((m): m is string => typeof m === 'string')
      : [];
    rows.push({
      label: 'Order picked by',
      value: matched.length > 0 ? `their words: ${matched.join(', ')}` : 'their words',
    });
  } else if (identKind === 'ambiguous') {
    const candidates = Array.isArray(facts.orderCandidates) ? facts.orderCandidates.length : 0;
    rows.push({
      label: 'Order picked by',
      value: `nobody yet — ambiguous, ${candidates} candidate${candidates === 1 ? '' : 's'} put to the customer`,
      tone: 'held',
    });
  } else if (identKind === 'general_question') {
    rows.push({ label: 'Order picked by', value: 'none needed — a general shipping question' });
  }

  // The id the customer wrote belongs to a sister brand (ops #1055) — the
  // reply is a redirect, and without this row the trace shows a not-found
  // with no explanation of why nothing on this account was discussed.
  const crossBrand = asRecord(facts.crossBrandOrder);
  const crossBrandId = asString(crossBrand?.orderId);
  const crossBrandName = asString(crossBrand?.brandName);
  if (crossBrandId && crossBrandName) {
    rows.push({
      label: 'Order from another brand',
      value: `${crossBrandId} looks like ${crossBrandName} — redirected, nothing on this account discussed`,
      tone: 'held',
    });
  }

  // The newest order was never charged, so the default stepped back to the
  // newest charged one (#1059) — without this row, "why is it not answering
  // about my newest order?" is undiagnosable from a screenshot.
  const skipped = asRecord(facts.skippedUnchargedLatest);
  const skippedId = asString(skipped?.orderId);
  if (skippedId) {
    const status = asString(skipped?.paymentStatus);
    rows.push({
      label: 'Newest order skipped',
      value: `${skippedId} was never charged${status ? ` (${status})` : ''} — answering about the newest paid order instead`,
      tone: 'held',
    });
  }

  // The payment state drives whole reply families (refunded disclosure, the
  // not-paid answer) and was invisible in the trace — #1043/#1048 could not
  // be diagnosed from ops screenshots because nothing showed what the run
  // believed the payment status WAS. Shown whenever known; 'Processed' too,
  // because "it said Processed" and "it said nothing" need different fixes.
  const paymentStatus = asString(facts.latestOrderPaymentStatus);
  if (paymentStatus) {
    const refundedAt = asString(facts.latestOrderRefundedAt);
    const fullyRefunded = facts.latestOrderAlreadyRefunded === true;
    const parts = [paymentStatus];
    if (fullyRefunded) parts.push('fully refunded on record');
    if (refundedAt) parts.push(`refunded ${refundedAt.slice(0, 10)}`);
    rows.push({
      label: 'Payment',
      value: parts.join(' · '),
      tone: paymentStatus === 'Processed' && !fullyRefunded ? 'fact' : 'held',
    });
  }

  const guarantee = asString(facts.guaranteeStatus);
  if (guarantee) {
    const window = asNumber(facts.guaranteeWindowDays);
    rows.push({
      label: 'Guarantee',
      value: window === null ? guarantee : `${guarantee} · ${window}-day window`,
      tone: guarantee === 'inside' ? 'fact' : 'stop',
    });
  }

  const subscriptions = asNumber(facts.activeSubscriptions);
  if (subscriptions !== null) {
    rows.push({
      label: 'Subscriptions',
      value: subscriptions === 0 ? 'None active' : `${subscriptions} active`,
    });
  }

  const signals = asRecord(facts.signals);
  if (signals?.chargebackFiled === true) {
    rows.push({ label: 'Dispute', value: 'Chargeback already filed', tone: 'stop' });
  } else if (signals?.chargebackThreat === true) {
    rows.push({ label: 'Dispute', value: 'Chargeback threatened', tone: 'held' });
  }

  const conditions = asRecord(facts.conditions);
  // Per order since 2026-10-19 (#1931078): the order under discussion, and
  // any other blocked orders listed by intake.
  const otherBlocked = Array.isArray(facts.blockedOrders)
    ? (facts.blockedOrders as unknown[]).flatMap((row) => {
        const id = asString(asRecord(row)?.orderId);
        return id ? [id] : [];
      })
    : [];
  if (conditions?.has_refund_block === true || otherBlocked.length > 0) {
    const parts = [
      ...(conditions?.has_refund_block === true ? ['On this order'] : []),
      ...(otherBlocked.length > 0 ? [`also on ${otherBlocked.join(', ')}`] : []),
    ];
    rows.push({
      label: 'Refund block',
      value: parts.join(' · '),
      tone: conditions?.has_refund_block === true ? 'stop' : 'held',
    });
  }

  const offer = asRecord(facts.offer);
  const offerPercent = asNumber(offer?.percent);
  const offerAmountCents = asNumber(offer?.amountCents);
  const noOfferReason = asString(offer?.none);

  if (offerPercent !== null) {
    rows.push({
      label: 'Authorised refund',
      value:
        offerAmountCents === null
          ? `${offerPercent}%`
          : `${offerPercent}% · ${formatUsdCents(offerAmountCents)}`,
      tone: 'held',
    });
  } else if (noOfferReason) {
    rows.push({
      label: 'Authorised refund',
      value: `None — ${noOfferReason.replace(/_/g, ' ')}`,
    });
  }

  // The reply delay (Admin -> Config): an automatic reply waiting to go out,
  // or one that stood down when its wait ended — without this row a held
  // reply looks exactly like one that was never sent.
  const hold = asRecord(facts.replyHold);
  const holdUntil = asString(hold?.until);
  if (holdUntil) {
    const dropped = asString(hold?.dropped);
    const at = new Date(holdUntil);
    const time = Number.isNaN(at.getTime())
      ? holdUntil
      : at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    rows.push(
      dropped
        ? {
            label: 'Held reply',
            value: `not sent — ${HOLD_DROPPED[dropped] ?? dropped.replace(/_/g, ' ')}`,
            tone: 'held',
          }
        : {
            label: 'Held reply',
            value: `goes out at ${time} unless the customer writes again or it is handed off`,
            tone: 'held',
          },
    );
  }

  const agent = asRecord(facts.agent);

  // A reply that exists only because the checker sent the first attempt back.
  // Whether the rewrite then passed shows in the usual places (a delivered
  // reply, or the blocked card) — this row records that a rule fired at all,
  // which is the signal that would otherwise vanish with the retry.
  const redrafts = asNumber(agent?.redrafts);
  if (redrafts !== null && redrafts > 0) {
    rows.push({
      label: 'Draft',
      value: 'rewritten once — a rule refused the first attempt',
    });
  }

  const indeterminate = Array.isArray(facts.indeterminate)
    ? facts.indeterminate.flatMap((item) => {
        const reason = asString(asRecord(item)?.reason);
        return reason ? [reason] : [];
      })
    : [];

  return {
    rows,
    proposedWrites: Array.isArray(agent?.proposedWrites)
      ? (agent.proposedWrites as ProposedWrite[])
      : [],
    violations: Array.isArray(agent?.violations) ? (agent.violations as RuleViolation[]) : [],
    offerPercent,
    offerAmountCents,
    noOfferReason,
    stopReason: asString(agent?.stopReason),
    rounds: asNumber(agent?.rounds),
    indeterminate,
  };
}

// ---------------------------------------------------------------------------
// Spend
// ---------------------------------------------------------------------------

export interface RoleSpend {
  role: string;
  inputTokens: number;
  outputTokens: number;
  /** Null when that model has no price configured. */
  costMicroUsd: number | null;
  latencyMs: number;
}

export interface RunSpend {
  inputTokens: number;
  outputTokens: number;
  costMicroUsd: number | null;
  latencyMs: number;
  perRole: RoleSpend[];
  /** Model ids nobody has priced. Empty when the run is fully costed. */
  unpriced: string[];
}

/**
 * The spend block the agent processor writes onto a run.
 *
 * Returns null for a run recorded before the instrumentation existed, so the
 * card is absent rather than showing a row of zeros for a ticket whose cost was
 * never measured. A zero would be a claim; absence is the truth.
 */
export function readSpend(raw: unknown): RunSpend | null {
  const spend = asRecord(asRecord(raw)?.spend);
  if (!spend) return null;

  const perRole = Array.isArray(spend.perRole)
    ? spend.perRole.flatMap((entry) => {
        const row = asRecord(entry);
        const role = asString(row?.role);
        if (!role) return [];
        return [
          {
            role,
            inputTokens: asNumber(row?.inputTokens) ?? 0,
            outputTokens: asNumber(row?.outputTokens) ?? 0,
            costMicroUsd: asNumber(row?.costMicroUsd),
            latencyMs: asNumber(row?.latencyMs) ?? 0,
          },
        ];
      })
    : [];

  return {
    inputTokens: asNumber(spend.inputTokens) ?? 0,
    outputTokens: asNumber(spend.outputTokens) ?? 0,
    // asNumber gives null for an absent or non-numeric value, which is exactly
    // the "unpriced" signal — it must not become a zero on the way through.
    costMicroUsd: asNumber(spend.costMicroUsd),
    latencyMs: asNumber(spend.latencyMs) ?? 0,
    perRole,
    unpriced: Array.isArray(spend.unpriced) ? spend.unpriced.map(String) : [],
  };
}
